import React from 'react';
import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Network, TrendingUp } from 'lucide-react';

interface Correlation {
  pattern1: string;
  pattern2: string;
  correlation: number;
  strength: 'Very Strong' | 'Strong' | 'Moderate' | 'Weak';
  occurrences: number;
  impact: number;
}

export const PatternCorrelation: React.FC = () => {
  const correlations: Correlation[] = [
    { 
      pattern1: 'Sequential Phones', 
      pattern2: 'Zip 80112', 
      correlation: 0.92, 
      strength: 'Very Strong',
      occurrences: 127,
      impact: 89.3
    },
    { 
      pattern1: 'Data Center IP', 
      pattern2: 'Sequential Phones', 
      correlation: 0.87, 
      strength: 'Strong',
      occurrences: 98,
      impact: 76.2
    },
    { 
      pattern1: 'Suspicious Email', 
      pattern2: 'Zip 80112', 
      correlation: 0.88, 
      strength: 'Strong',
      occurrences: 89,
      impact: 72.1
    },
    { 
      pattern1: 'Velocity Attack', 
      pattern2: 'Data Center IP', 
      correlation: 0.84, 
      strength: 'Strong',
      occurrences: 67,
      impact: 68.9
    },
    { 
      pattern1: 'Sequential Phones', 
      pattern2: 'Suspicious Email', 
      correlation: 0.73, 
      strength: 'Moderate',
      occurrences: 54,
      impact: 45.2
    },
    { 
      pattern1: 'Velocity Attack', 
      pattern2: 'Sequential Phones', 
      correlation: 0.69, 
      strength: 'Moderate',
      occurrences: 43,
      impact: 38.7
    }
  ];

  const getCorrelationColor = (value: number) => {
    if (value >= 0.85) return '#dc2626'; // red-600
    if (value >= 0.75) return '#ea580c'; // orange-600
    if (value >= 0.65) return '#d97706'; // amber-600
    return '#65a30d'; // lime-600
  };

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'Very Strong':
        return 'bg-red-100 text-red-800';
      case 'Strong':
        return 'bg-orange-100 text-orange-800';
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const chartData = correlations.map(corr => ({
    name: `${corr.pattern1.split(' ')[0]} ↔ ${corr.pattern2.split(' ')[0]}`,
    fullName: `${corr.pattern1} ↔ ${corr.pattern2}`,
    correlation: corr.correlation,
    percentage: Math.round(corr.correlation * 100),
    occurrences: corr.occurrences
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg max-w-xs">
          <p className="text-sm font-medium">{data.fullName}</p>
          <p className="text-sm">Correlation: {data.percentage}%</p>
          <p className="text-sm">Co-occurrences: {data.occurrences}</p>
        </div>
      );
    }
    return null;
  };

  const topCorrelation = correlations[0];
  const avgCorrelation = correlations.reduce((sum, c) => sum + c.correlation, 0) / correlations.length;

  return (
    <div className="space-y-6">
      {/* Header Card with Key Insights */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Network className="w-6 h-6 text-purple-500" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Pattern Correlations</h3>
            <p className="text-sm text-muted-foreground">How fraud patterns occur together</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-600">{Math.round(topCorrelation.correlation * 100)}%</div>
            <div className="text-sm text-red-800">Strongest Correlation</div>
            <div className="text-xs text-muted-foreground mt-1">
              {topCorrelation.pattern1} + {topCorrelation.pattern2}
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{Math.round(avgCorrelation * 100)}%</div>
            <div className="text-sm text-blue-800">Average Correlation</div>
            <div className="text-xs text-muted-foreground mt-1">Across all patterns</div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">
              {correlations.reduce((sum, c) => sum + c.occurrences, 0)}
            </div>
            <div className="text-sm text-green-800">Total Co-occurrences</div>
            <div className="text-xs text-muted-foreground mt-1">This month</div>
          </div>
        </div>

        {/* Correlation Chart */}
        <div className="mb-4">
          <h4 className="font-medium text-foreground mb-3">Correlation Strength</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="percentage" 
                fill="#8884d8"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Detailed Correlations */}
      <Card className="p-6">
        <h4 className="font-medium text-foreground mb-4">Detailed Pattern Relationships</h4>
        <div className="space-y-4">
          {correlations.map((corr, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground mb-1">
                    {corr.pattern1} ↔ {corr.pattern2}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{corr.occurrences} co-occurrences</span>
                    <span>•</span>
                    <span>{corr.impact}% of fraud cases</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-foreground">
                    {(corr.correlation * 100).toFixed(0)}%
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStrengthColor(corr.strength)}`}>
                    {corr.strength}
                  </span>
                </div>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="h-3 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${corr.correlation * 100}%`,
                    backgroundColor: getCorrelationColor(corr.correlation)
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Insights */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <h5 className="font-medium text-blue-800 mb-2">Key Insights</h5>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Sequential phones + Zip 80112</strong> appears in 92% of fraud cases together</li>
                <li>• <strong>Data center IPs</strong> strongly correlate with automated attacks</li>
                <li>• <strong>Email domain patterns</strong> often cluster by geographic location</li>
                <li>• <strong>Velocity attacks</strong> typically involve multiple correlated patterns</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};